import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Card, Row, Col } from 'ALIASED-antd';
import useApi from 'vl-common/src/hooks/useApi';
import DisplayAvailableSlotsRandox from 'vl-common/src/components/Face2FaceBooking/DisplayAvailableSlotsRandox';
import { LabelValuePairs } from 'vl-common/src/components/Face2FaceBooking/ChooseFace2FaceClinician';
import { getRandoxClinics } from 'vl-common/src/schemas';
import { parseRandoxClinicSlotUtc } from 'vl-common/src/components/Face2FaceBooking/helpers';
import styled from '@emotion/styled';

const { Search } = Input;

export type RandoxClinicType = getRandoxClinics.Response['closestClinics'][0];

export type ClinicSlotSurveyData = {
  id: string;
  name: string;
  postcode: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  distance: number;
  slotId: string;
  date: string;
  time: string;
  displayTime?: string;
};

type RandoxSearchInputProps = {
  onSelectedClinicSlot: (clinicName: ClinicSlotSurveyData | undefined) => void;
  setClinicsNotFound?: (value: boolean) => void;
  isAdminPortal?: boolean;
};

const StyledSearchWrapper = styled.div`
  max-width: 559px;
  width: 100%;
  margin-bottom: 20px;

  .ant-input-search {
    button {
      padding: 0 !important;
    }
  }
`;

const StyledSlotWrapper = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  justify-content: center;

  span {
    background-color: var(--color-Alabaster);
    border-radius: 6px;
    padding: 6px 12px;
    border: 3px solid #e8e8e8;
    font-size: 15px;
  }
`;

const RandoxSearchInput = ({ onSelectedClinicSlot, isAdminPortal, setClinicsNotFound }: RandoxSearchInputProps) => {
  const { t } = useTranslation('common');
  const { Api, consumed } = useApi();
  const [foundLocations, setFoundLocations] = useState<RandoxClinicType[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedSlotString, setSelectedSlotString] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetClinicSlot = () => {
    setSelectedId('');
    onSelectedClinicSlot(undefined);
    setSelectedSlotString(undefined);
    setClinicsNotFound?.(false);
  };

  const fetchClinicLocations = useCallback(
    async (searchPostCode: string = '') => {
      if (!searchPostCode) {
        setErrorMessage('A postcode must be provided');
        return;
      }
      resetClinicSlot();
      setLoading(true);
      await Api.randox
        .getRandoxClinicLocations({
          postcode: searchPostCode
        })
        .then((res) => {
          setErrorMessage(null);
          if (!res?.data?.closestClinics?.length) {
            setClinicsNotFound?.(true);
          }
          setFoundLocations(res?.data?.closestClinics || []);
          if (!res?.data?.closestClinics?.length) {
            setErrorMessage('No clinics found in this area. Try a different postcode or appointment option.');
          }
        })
        .catch((e) => {
          consumed(e);
          setClinicsNotFound?.(true);
          setErrorMessage('Invalid postcode. Try a different postcode or appointment option.');
        })
        .finally(() => setLoading(false));
    },
    [Api]
  );

  const setSelectedSlot = (slotInfo: ClinicSlotSurveyData) => {
    setSelectedId(slotInfo.slotId);
    onSelectedClinicSlot(slotInfo);

    const getSelectedSlotString = () => {
      try {
        const dateTime = parseRandoxClinicSlotUtc(slotInfo.date, slotInfo.time).setZone('local');
        return `${slotInfo.name} on ${dateTime.toFormat("cccc, d MMMM yyyy 'at' h:mm a")}`;
      } catch (error) {
        console.error('Date formatting error:', error);
        return `${slotInfo.name} on ${slotInfo.date} at ${slotInfo.time}`;
      }
    };

    setSelectedSlotString(getSelectedSlotString());
  };

  return (
    <div style={{ width: '100%' }}>
      <StyledSearchWrapper>
        <Search
          placeholder={t('Search for clinics closest to your postcode')}
          onSearch={fetchClinicLocations}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          loading={loading}
          disabled={loading}
        />
      </StyledSearchWrapper>
      <div
        style={
          isAdminPortal
            ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }
            : { display: 'flex', flexDirection: 'column', gap: 16 }
        }
      >
        {errorMessage && <div style={{ color: 'rgb(255, 0, 0)' }}>{errorMessage}</div>}
        {!errorMessage &&
          foundLocations?.map((location) => (
            <Card key={location.Id} title={location?.Name} styles={{ body: { padding: 0 } }}>
              <Row>
                <Col span={24}>
                  <LabelValuePairs
                    values={[
                      { label: 'Name', value: location?.Name },
                      { label: 'Distance', value: `${location?.distanceInMiles} miles` },
                      { label: 'Town/City', value: location?.townCity },
                      {
                        label: 'Address',
                        value: [
                          location?.addressLine1,
                          location?.addressLine2,
                          location?.townCity,
                          location?.postalCode
                        ]
                          .filter(Boolean)
                          .join(', ')
                      }
                    ]}
                  />
                </Col>

                <Col span={24}>
                  <DisplayAvailableSlotsRandox
                    selectedId={selectedId}
                    location={location}
                    setSelectedSlot={setSelectedSlot}
                  />
                </Col>
              </Row>
            </Card>
          ))}
      </div>
      {selectedSlotString && (
        <StyledSlotWrapper>
          <span>
            <b>{t('Selected slot:')}</b> {selectedSlotString}
          </span>
        </StyledSlotWrapper>
      )}
    </div>
  );
};

export default RandoxSearchInput;
